import { useEffect, useMemo, useState } from 'react';
import { Clock, FileText, Globe, Search, Loader2 } from 'lucide-react';
import { listReviewRuns, getLatestPublicDataRun, type ReviewRun, type PublicDataRun } from '../api/review';
import { listCaseRequests, type CaseRequest } from '../api/caseRequests';

// Case-review observability (#A, operator 2026-11-04): a compact card at
// the top of the Event Log tab showing the started/completed timestamps +
// duration of each case-review phase so the user can see, while reviewing,
// how long each phase took. All timestamps already exist in the backend
// (case_requests.created_at/completed_at, review_runs.started_at/
// completed_at, public_data_runs.started_at/completed_at) — this card only
// reuses the existing list endpoints, no backend change.

interface CaseTimelineCardProps {
  caseId: string;
  // Bumped by the parent on case refresh so the card re-fetches.
  refreshKey?: number;
}

interface PhaseRow {
  label: string;
  icon: typeof Clock;
  started: string | null;
  completed: string | null;
  running: boolean;
}

function fmtDurationMs(ms: number | null): string {
  if (ms == null || !isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 1) return '<1s';
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function fmtTimestamp(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function diffMs(started: string | null, completed: string | null): number | null {
  if (!started || !completed) return null;
  const a = new Date(started).getTime();
  const b = new Date(completed).getTime();
  if (isNaN(a) || isNaN(b)) return null;
  return b - a;
}

export default function CaseTimelineCard({ caseId, refreshKey }: CaseTimelineCardProps) {
  const [docDrops, setDocDrops] = useState<CaseRequest[]>([]);
  const [reviews, setReviews] = useState<ReviewRun[]>([]);
  const [publicRun, setPublicRun] = useState<PublicDataRun | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listCaseRequests(caseId, { include: 'pending,recent', limit: 50 })
        .then((res) => (res.requests || []).filter((r) => r.kind === 'doc_drop'))
        .catch(() => [] as CaseRequest[]),
      listReviewRuns(caseId)
        .then((res) => res.items || [])
        .catch(() => [] as ReviewRun[]),
      getLatestPublicDataRun(caseId).catch(() => null),
    ]).then(([drops, runs, pub]) => {
      if (cancelled) return;
      setDocDrops(drops);
      setReviews(runs);
      setPublicRun(pub);
      setLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, refreshKey]);

  const ingestionRows: PhaseRow[] = useMemo(() => {
    // Most-recent-first doc_drop rows. Each is one ingestion phase.
    const sorted = [...docDrops].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
    return sorted.map((r) => {
      const filename =
        (r.request_payload as { filename?: string } | null)?.filename || 'file';
      return {
        label: `Ingestion — ${filename}`,
        icon: FileText,
        started: r.created_at,
        completed: r.completed_at,
        running: r.status === 'pending',
      };
    });
  }, [docDrops]);

  const reviewRow: PhaseRow | null = useMemo(() => {
    if (reviews.length === 0) return null;
    // Latest by run_number (listReviewRuns is ordered server-side, but
    // sort defensively by run_number desc + started_at desc).
    const sorted = [...reviews].sort((a, b) => {
      if (b.run_number !== a.run_number) return b.run_number - a.run_number;
      const ta = a.started_at ? new Date(a.started_at).getTime() : 0;
      const tb = b.started_at ? new Date(b.started_at).getTime() : 0;
      return tb - ta;
    });
    const r = sorted[0];
    return {
      label: `Case review — run #${r.run_number}`,
      icon: Search,
      started: r.started_at,
      completed: r.completed_at,
      running: r.status === 'RUNNING',
    };
  }, [reviews]);

  const publicRow: PhaseRow | null = useMemo(() => {
    if (!publicRun || !publicRun.started_at) return null;
    return {
      label: 'Public data pull',
      icon: Globe,
      started: publicRun.started_at,
      completed: publicRun.completed_at,
      running: publicRun.status === 'RUNNING',
    };
  }, [publicRun]);

  // Order: ingestion(s) -> public data pull -> case review (the natural
  // pipeline order). Missing phases are omitted (no empty "—" rows) to keep
  // the card compact; the raw audit-log feed below already shows every
  // action.
  const rows: PhaseRow[] = [
    ...ingestionRows,
    ...(publicRow ? [publicRow] : []),
    ...(reviewRow ? [reviewRow] : []),
  ];

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm mb-4">
        <Loader2 size={16} className="animate-spin" /> Loading case timeline…
      </div>
    );
  }

  if (rows.length === 0) {
    // No phase data yet — nothing to show. Keep the card invisible so the
    // Event Log tab opens straight to the audit feed.
    return null;
  }

  return (
    <div className="mb-5 rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50">
        <Clock size={15} className="text-primary-500" />
        <h3 className="text-sm font-semibold text-slate-700">Case Timeline</h3>
        <span className="text-xs text-slate-400">phase durations</span>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((row, i) => {
          const Icon = row.icon;
          const dur = row.running
            ? `${fmtDurationMs(
                Date.now() - (row.started ? new Date(row.started).getTime() : Date.now()),
              )} so far`
            : fmtDurationMs(diffMs(row.started, row.completed));
          return (
            <div key={i} className="flex items-start gap-3 px-4 py-2.5">
              <Icon size={16} className="mt-0.5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-700 truncate">{row.label}</span>
                  <span
                    className={
                      'text-xs px-1.5 py-0.5 rounded font-medium ' +
                      (row.running
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-slate-100 text-slate-600')
                    }
                  >
                    {row.running ? (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 size={10} className="animate-spin" /> in progress
                      </span>
                    ) : (
                      `took ${dur}`
                    )}
                  </span>
                </div>
                <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
                  <span>started: {fmtTimestamp(row.started)}</span>
                  <span>completed: {fmtTimestamp(row.completed)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}